import React, { Component } from 'react';
import {
    getStorage, 
    ref, 
    list, 
    getDownloadURL, 
    getMetadata,
    ListResult,
    uploadBytes,
    deleteObject,
} from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { DeleteOutline, InsertDriveFile, Folder } from '@mui/icons-material';
import { Button } from './Button';
import { Theme } from '../Theme';

const storage = getStorage(); // app already initialised elsewhere

export type FileBrowserProps = {
    basePath?: string; // Optional base path to override URL-based path detection
    onFileSelect?: (fileUrl: string, fileName: string, filePath: string) => void; // Callback when file is selected
    onFileDeleted?: (filePath: string) => void;
    style?: React.CSSProperties; // Optional style prop
};

type FileBrowserNode = {
    type: 'folder' | 'file';
    path: string;
    name: string;
    size: number;
    lastModified: number;
    url: string;
    children: Array<FileBrowserNode>;
}

type FileBrowserState = {
    root:  FileBrowserNode;
    loading: boolean;
    selectedFilePath?: string;
    selectedFolderPath?: string;
};

export class FileBrowser extends Component<FileBrowserProps, FileBrowserState> {
    private fileInputRef = React.createRef<HTMLInputElement>();

    constructor(props: FileBrowserProps) {
        super(props);
        this.state = {
            root: {
                type: 'folder',
                path: '',
                name: '',
                size: 0,
                lastModified: 0,
                url: '',
                children: []
            },
            loading: false,
            selectedFilePath: undefined,
            selectedFolderPath: undefined,
        };
    }

    componentDidMount() {
        this.fetchFolder('');
    }

    private async fetchFolder(path: string) {
        console.log(`FILE_BROWSER: fetchFolder("${path}")`);
        this.setState({ loading: true });

        try {
            // Determine the full path to fetch from
            const basePath = this.props.basePath || '';
            const fullPath = basePath ? (path ? `${basePath}/${path}` : basePath) : path;
            
            // Create a reference to the storage location
            const storageRef = ref(storage, fullPath);
            
            // List all items in this location
            const listResult: ListResult = await list(storageRef);
            
            // Process folders (prefixes) recursively
            const folderPromises = listResult.prefixes.map(async (prefixRef) => {
                const folderName = prefixRef.name.split('/').pop() || prefixRef.name;
                const relativePath = path ? `${path}/${folderName}` : folderName;
                
                // Recursively fetch children for this folder
                const children = await this.fetchFolderContents(relativePath);
                
                const folderNode: FileBrowserNode = {
                    type: 'folder',
                    path: relativePath,
                    name: folderName,
                    size: 0,
                    lastModified: 0,
                    url: '',
                    children: children
                };
                
                return folderNode;
            });
            
            // Process files (items)
            const filePromises = listResult.items
                .filter((itemRef) => itemRef.name !== '.keep' && !itemRef.name.endsWith('/'))
                .map(async (itemRef) => {
                const fileName = itemRef.name.split('/').pop() || itemRef.name;
                const relativePath = path ? `${path}/${fileName}` : fileName;
                
                // Get file metadata and download URL
                const metadata = await getMetadata(itemRef);
                const downloadUrl = await getDownloadURL(itemRef);
                
                const fileNode: FileBrowserNode = {
                    type: 'file',
                    path: relativePath,
                    name: fileName,
                    size: metadata.size || 0,
                    lastModified: new Date(metadata.timeCreated || metadata.updated || '').getTime(),
                    url: downloadUrl,
                    children: []
                };
                
                return fileNode;
                });
            
            // Wait for all folders and files to be processed
            const [folders, files] = await Promise.all([
                Promise.all(folderPromises),
                Promise.all(filePromises)
            ]);
            
            // Combine folders and files
            const allChildren = [...folders, ...files];
            
            // If this is the root call (empty path), update the root state
            if (!path) {
                const rootNode: FileBrowserNode = {
                    type: 'folder',
                    path: '',
                    name: 'Root',
                    size: 0,
                    lastModified: 0,
                    url: '',
                    children: allChildren
                };
                
                this.setState({ 
                    root: rootNode,
                    loading: false 
                });
            }
            
            return allChildren;
            
        } catch (error) {
            console.error(`FILE_BROWSER: Error fetching folder "${path}":`, error);
            this.setState({ loading: false });
            return [];
        }
    }

    // Helper method for recursive folder content fetching
    private async fetchFolderContents(path: string): Promise<FileBrowserNode[]> {
        try {
            // Determine the full path to fetch from
            const basePath = this.props.basePath || '';
            const fullPath = basePath ? `${basePath}/${path}` : path;
            
            // Create a reference to the storage location
            const storageRef = ref(storage, fullPath);
            
            // List all items in this location
            const listResult: ListResult = await list(storageRef);
            
            // Process folders (prefixes) recursively
            const folderPromises = listResult.prefixes.map(async (prefixRef) => {
                const folderName = prefixRef.name.split('/').pop() || prefixRef.name;
                const relativePath = `${path}/${folderName}`;
                
                // Recursively fetch children for this folder
                const children = await this.fetchFolderContents(relativePath);
                
                const folderNode: FileBrowserNode = {
                    type: 'folder',
                    path: relativePath,
                    name: folderName,
                    size: 0,
                    lastModified: 0,
                    url: '',
                    children: children
                };
                
                return folderNode;
            });
            
            // Process files (items)
            const filePromises = listResult.items
                .filter((itemRef) => itemRef.name !== '.keep' && !itemRef.name.endsWith('/'))
                .map(async (itemRef) => {
                const fileName = itemRef.name.split('/').pop() || itemRef.name;
                const relativePath = `${path}/${fileName}`;
                
                // Get file metadata and download URL
                const metadata = await getMetadata(itemRef);
                const downloadUrl = await getDownloadURL(itemRef);
                
                const fileNode: FileBrowserNode = {
                    type: 'file',
                    path: relativePath,
                    name: fileName,
                    size: metadata.size || 0,
                    lastModified: new Date(metadata.timeCreated || metadata.updated || '').getTime(),
                    url: downloadUrl,
                    children: []
                };
                
                return fileNode;
                });
            
            // Wait for all folders and files to be processed
            const [folders, files] = await Promise.all([
                Promise.all(folderPromises),
                Promise.all(filePromises)
            ]);
            
            // Combine folders and files
            return [...folders, ...files];
            
        } catch (error) {
            console.error(`FILE_BROWSER: Error fetching folder contents "${path}":`, error);
            return [];
        }
    }

    private getFullPath(relativePath: string): string {
        if (!this.props.basePath) {
            return relativePath;
        }

        return relativePath ? `${this.props.basePath}/${relativePath}` : this.props.basePath;
    }

    private getUploadTargetFolderPath(): string {
        return this.state.selectedFolderPath || '';
    }

    private async ensureFreshAuthToken() {
        const auth = getAuth();
        if (auth.currentUser) {
            await auth.currentUser.getIdToken(true);
        }
    }

    private handleFolderSelect = (folderPath: string) => {
        this.setState(() => ({
            selectedFolderPath: folderPath,
            selectedFilePath: undefined,
        }));
    }

    private handleFileSelect = (node: FileBrowserNode) => {
        this.setState({
            selectedFilePath: node.path,
        });

        if (this.props.onFileSelect) {
            this.props.onFileSelect(node.url, node.name, node.path);
        } else {
            window.open(node.url, '_blank');
        }
    }

    private handleUploadClick = () => {
        this.fileInputRef.current?.click();
    }

    private handleUploadFile = async (file?: File) => {
        if (!file) {
            return;
        }

        const targetFolderPath = this.getUploadTargetFolderPath();
        const relativeTargetPath = targetFolderPath ? `${targetFolderPath}/${file.name}` : file.name;

        try {
            this.setState({ loading: true });
            await this.ensureFreshAuthToken();
            await uploadBytes(ref(storage, this.getFullPath(relativeTargetPath)), file);
            await this.fetchFolder('');
        } catch (error) {
            console.error('FILE_BROWSER: handleUploadFile(): Error uploading file:', error);
            window.alert(error instanceof Error ? error.message : 'Failed to upload file.');
            this.setState({ loading: false });
        }
    }

    private handleCreateFolder = async () => {
        const folderName = window.prompt('Enter the new folder name:')?.trim();
        if (!folderName) {
            return;
        }

        if (folderName.includes('/')) {
            window.alert('Folder names cannot contain "/".');
            return;
        }

        const targetFolderPath = this.getUploadTargetFolderPath();
        const newFolderPath = targetFolderPath ? `${targetFolderPath}/${folderName}` : folderName;

        try {
            this.setState({ loading: true });
            await this.ensureFreshAuthToken();
            await uploadBytes(ref(storage, this.getFullPath(`${newFolderPath}/.keep`)), new Uint8Array());
            await this.fetchFolder('');
            this.setState({ selectedFolderPath: newFolderPath });
        } catch (error) {
            console.error('FILE_BROWSER: handleCreateFolder(): Error creating folder:', error);
            window.alert(error instanceof Error ? error.message : 'Failed to create folder.');
            this.setState({ loading: false });
        }
    }

    private handleDeleteFile = async (node: FileBrowserNode) => {
        const confirmed = window.confirm(`Delete "${node.name}"?`);
        if (!confirmed) {
            return;
        }

        try {
            this.setState({ loading: true });
            await this.ensureFreshAuthToken();
            await deleteObject(ref(storage, this.getFullPath(node.path)));
            await this.fetchFolder('');

            if (this.state.selectedFilePath === node.path) {
                this.setState({ selectedFilePath: undefined });
            }

            this.props.onFileDeleted?.(node.path);
        } catch (error) {
            console.error('FILE_BROWSER: handleDeleteFile(): Error deleting file:', error);
            window.alert(error instanceof Error ? error.message : 'Failed to delete file.');
            this.setState({ loading: false });
        }
    }

    private renderTree(nodes: FileBrowserNode[] = this.state.root.children): React.ReactNode {
        return nodes.map(node => {
            if (node.type === 'folder') {
                const isSelected = this.state.selectedFolderPath === node.path;
                return (
                    <TreeItem
                        key={node.path}
                        itemId={node.path}
                        label={
                            <span
                                onClick={() => this.handleFolderSelect(node.path)}
                                style={{
                                    ...styles.treeLabel,
                                    backgroundColor: isSelected ? 'rgba(129, 126, 228, 0.15)' : 'transparent',
                                }}
                            >
                                <Folder 
                                    htmlColor='#817ee4d0' 
                                    fontSize='medium' 
                                    style={{ 
                                        paddingBottom: '2px', 
                                        marginRight: '4px', 
                                        verticalAlign: 'middle' 
                                    }} 
                                />
                                {node.name}
                            </span>
                        }
                    >
                        {node.children.length > 0 && this.renderTree(node.children)}
                    </TreeItem>
                );
            } else {
                const isSelected = this.state.selectedFilePath === node.path;
                return (
                    <TreeItem
                        key={node.path}
                        itemId={node.path}
                        label={
                            <div
                                style={{
                                    ...styles.treeLabel,
                                    justifyContent: 'space-between',
                                    backgroundColor: isSelected ? 'rgba(129, 126, 228, 0.15)' : 'transparent',
                                }}
                            >
                                <span
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    this.handleFileSelect(node);
                                }}
                                style={{ 
                                    cursor: 'pointer', 
                                    textDecoration: 'underline',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <InsertDriveFile 
                                    htmlColor='#817ee4d0' 
                                    fontSize='medium' 
                                    style={{ 
                                        paddingBottom: '2px', 
                                        marginRight: '4px', 
                                        verticalAlign: 'middle' 
                                    }} 
                                />
                                {node.name}
                            </span>
                                <button
                                    type='button'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        void this.handleDeleteFile(node);
                                    }}
                                    style={styles.deleteButton}
                                    aria-label={`Delete ${node.name}`}
                                >
                                    <DeleteOutline fontSize='small' />
                                </button>
                            </div>
                        }
                    />
                );
            }
        });
    }

    public render(): React.ReactNode {
        const { style } = this.props;
        
        console.log(`FILE_BROWSER: root: ${JSON.stringify(this.state.root)}`);

        return (
            <div style={{ ...styles.container, ...style }}>
                <div style={styles.actionsBar}>
                    <div style={styles.selectedFolderText}>
                        Target folder: {this.state.selectedFolderPath || 'Root'}
                    </div>
                    <div style={styles.actionsButtons}>
                        <input
                            ref={this.fileInputRef}
                            type='file'
                            style={styles.hiddenInput}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                void this.handleUploadFile(file);
                                e.target.value = '';
                            }}
                        />
                        <Button onClick={this.handleUploadClick} label='Upload' type='secondary' disabled={this.state.loading} />
                        <Button onClick={this.handleCreateFolder} label='Create folder' type='secondary' disabled={this.state.loading} />
                    </div>
                </div>
                <SimpleTreeView
                    aria-label='Firebase files'
                >
                    {this.renderTree()}
                </SimpleTreeView>
            </div>
        );
    }
}

const styles = {
    container: {
        width: '100%',
    },
    actionsBar: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
        marginBottom: '16px',
    },
    actionsButtons: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap' as const,
    },
    selectedFolderText: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
    },
    treeLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 6px',
        borderRadius: '6px',
        width: '100%',
        boxSizing: 'border-box' as const,
    },
    deleteButton: {
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        color: Theme.Colours.text,
        display: 'flex',
        alignItems: 'center',
        padding: '2px',
        marginLeft: '8px',
    },
    hiddenInput: {
        display: 'none',
    },
};
